import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inboxApi, type Template } from '@wa/shared';

/**
 * The composer, outside the 24-hour window.
 *
 * <b>What this is.</b> Outside the window nothing free-form can be sent — Meta
 * refuses it. The honest control is a template picker, which is what this screen
 * had, and it is also where an agent stops: they wanted to answer somebody, and
 * instead they are being asked to choose a marketing artefact.
 *
 * The design being replicated nominates <i>one</i> template — utility, one body
 * variable — and renders it as though it were the composer. The agent sees the
 * template's fixed opening line, types into the gap, and sees the fixed closing
 * line. What goes out is the approved template with the variable filled.
 *
 * <b>What it is not.</b> It is not a way to message people who have not asked. The
 * template is still approved, the send is still billed as a conversation, and a
 * utility template used to open unsolicited conversations is exactly what gets a
 * number's quality rating cut and then its sending limit — which this account has
 * already been through once. So the cost is stated on the control rather than
 * hidden behind it, and the template picker stays one click away for everything
 * this one template does not cover.
 *
 * Falls back to the picker, with a reason, when no template is nominated or the
 * nominated one has been deleted or unapproved in Meta's console. The server
 * returns null in both cases rather than an id that would fail at send time.
 */
interface Props {
  isSending: boolean;
  onSendTemplate: (templateId: number, variables: string[]) => void;
  onOpenTemplates: () => void;
}

export function SessionOpener({ isSending, onSendTemplate, onOpenTemplates }: Props) {
  const [value, setValue] = useState('');

  const settings = useQuery({ queryKey: ['settings'], queryFn: inboxApi.settings, staleTime: 300_000 });
  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates, staleTime: 300_000 });

  const openerId = settings.data?.sessionOpenerTemplateId ?? null;
  const opener = openerId === null
    ? undefined
    : (templates.data ?? []).find((t) => t.id === openerId);

  // One body variable exactly. Zero has no gap to type into, and two would need a
  // form — at which point it is the template picker, which already exists and is
  // better at it.
  const usable = opener !== undefined && opener.bodyVariables === 1;

  if (settings.isPending || templates.isPending) {
    return <Shell><p className="text-small text-(--color-muted)">Loading…</p></Shell>;
  }

  if (!usable) {
    return (
      <Shell>
        <p className="text-small text-(--color-muted)">
          {openerId === null
            ? 'This conversation is outside the 24-hour window, so only an approved template can reopen it.'
            : opener === undefined
              ? 'The template set up for reopening chats is no longer approved in Meta’s console, so only the picker can reopen this.'
              : `“${opener.name}” is set up for reopening chats but takes ${opener.bodyVariables} variables, and this composer fills exactly one.`}
        </p>
        <button
          type="button"
          onClick={onOpenTemplates}
          className="tap mt-2 rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-(--color-brand-ink)"
        >
          Send a template
        </button>
        {openerId === null && (
          <p className="mt-2 text-micro text-(--color-faint)">
            An administrator can nominate one template under Settings, and this box
            becomes a message box.
          </p>
        )}
      </Shell>
    );
  }

  // The template's own body, split at its one placeholder. Everything before the
  // gap and everything after it are fixed text the agent cannot change — which is
  // the whole point, and why they are rendered as text rather than as inputs.
  const [before, after] = splitOnPlaceholder(opener.bodyText ?? '{{1}}');

  return (
    <Shell>
      {opener.headerText && (
        <p className="mb-1 text-body font-medium text-(--color-ink)">{opener.headerText}</p>
      )}

      {before.trim() && (
        <p className="text-body whitespace-pre-wrap text-(--color-ink)">{before.trimEnd()}</p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onSendTemplate(opener.id, [value.trim()]);
        }}
        className="mt-1 flex items-end gap-2"
      >
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Type message"
          aria-label={`The one thing you can change in “${opener.name}”`}
          className="h-9 min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-2.5 text-body outline-none focus:border-(--color-brand) focus:bg-(--color-surface)"
        />
        <button
          type="submit"
          disabled={!value.trim() || isSending}
          aria-label="Send"
          className="tap grid size-9 shrink-0 place-items-center rounded-(--radius-field) text-(--color-brand) hover:bg-(--color-brand-wash) disabled:opacity-40"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M3.4 10 16.6 4.2 13.4 16.4l-3.6-4.2-6.4-2.2z" fill="none" stroke="currentColor"
                  strokeWidth="1.6" strokeLinejoin="round" />
          </svg>
        </button>
      </form>

      {after.trim() && (
        <p className="mt-1 text-body whitespace-pre-wrap text-(--color-muted) italic">{after.trimStart()}</p>
      )}

      {opener.footerText && (
        <p className="mt-1 text-micro text-(--color-faint)">{opener.footerText}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* The cost, on the control rather than behind it. This send opens a
            billed conversation whatever the agent believes they are doing, and an
            agent who finds that out from an invoice was misled by their tools. */}
        <p className="text-micro text-(--color-faint)">
          Sends the approved template “{opener.name}” and starts a paid conversation.
        </p>
        <button
          type="button"
          onClick={onOpenTemplates}
          className="tap ml-auto text-micro font-medium text-(--color-brand) underline-offset-2 hover:underline"
        >
          or send a different template
        </button>
      </div>
    </Shell>
  );
}

/**
 * Splits a template body at its first placeholder.
 *
 * Meta writes them as `{{1}}`, and a body whose placeholder is missing is a body
 * this composer cannot fill — the caller has already checked `bodyVariables === 1`,
 * so the fallback here is for a template whose count and text disagree, which the
 * sync can produce and which must not render as an empty box with no gap in it.
 */
function splitOnPlaceholder(body: string): [string, string] {
  const at = body.search(/\{\{\s*1\s*\}\}/);
  if (at < 0) return [body, ''];
  const end = body.indexOf('}}', at) + 2;
  return [body.slice(0, at), body.slice(end)];
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center gap-1.5 pb-1.5 text-small">
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" className="shrink-0 text-(--color-bad)">
          <circle cx="10" cy="10" r="7.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 6.2v4l2.6 1.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <path d="m3.4 3.4 13.2 13.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="font-medium text-(--color-ink-soft)">Send a message</span>
        <span className="text-(--color-muted)">to customer in closed window</span>
      </div>

      <div className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-3">
        {children}
      </div>
    </div>
  );
}

export type { Template };
